import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Sugarcane ke liye kya best hai?",
  "Mini sprinkler price kaise milegi?",
  "Drip ya sprinkler — kya loon?",
];

function getSessionId() {
  let id = localStorage.getItem("jamna_chat_session");
  if (!id) {
    id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("jamna_chat_session", id);
  }
  return id;
}

export default function ChatAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Namaste! Main Jamna AI hoon. Pipe, sprinkler ya drip — jo bhi poochna ho, bataiye." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (resp.status === 429) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: "Aaj ki chat limit poori ho gayi. Kal dobara baat karte hain! Turant baat ke liye call karein: +91 98290 57721" };
          return copy;
        });
        setBusy(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          try {
            const data = JSON.parse(part.slice(5).trim());
            if (data.t) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + data.t };
                return copy;
              });
            }
            if (data.lead) {
              setMessages((m) => [
                ...m,
                {
                  role: "assistant",
                  text: `Dhanyavaad ${data.lead.name}! Aapki enquiry save ho gayi hai (${data.lead.phone}). Hamari team jald hi call karegi.`,
                  lead: true,
                },
              ]);
            }
            if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: "Maaf kijiye, abhi jawab nahi de pa raha. Thodi der baad try karein." };
                return copy;
              });
            }
          } catch { /* partial json */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Connection issue ho gaya. Dobara try karein." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-assistant-button"
        aria-label="AI Assistant"
        className="fixed bottom-6 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-harvest text-forest shadow-xl shadow-[#0B1A33]/20 transition-transform duration-300 hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chat-assistant-panel"
            className="fixed bottom-24 right-4 z-[70] flex h-[480px] w-[92vw] max-w-sm flex-col overflow-hidden rounded-2xl border border-line bg-forest shadow-2xl shadow-[#0B1A33]/30 sm:right-6"
          >
            <div className="border-b border-line bg-moss px-5 py-4">
              <p className="font-display text-lg font-semibold tracking-tight text-cream">Jamna AI Assistant</p>
              <p className="text-[11px] uppercase tracking-[0.15em] text-sage">Products • Suggestions • Hinglish</p>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${i}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-harvest text-forest"
                        : "border border-line bg-moss text-cream"
                    }`}
                  >
                    {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 px-4 pb-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="chat-suggestion"
                    className="border border-line px-3 py-1.5 text-[11px] text-sage transition-colors duration-300 hover:border-harvest hover:text-harvest"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 border-t border-line p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Apna sawaal likhein…"
                data-testid="chat-input"
                className="flex-1 border border-line bg-moss px-3.5 py-2.5 text-sm text-cream outline-none transition-colors duration-300 focus:border-harvest"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="chat-send-button"
                className="bg-harvest p-2.5 text-forest transition-colors duration-300 hover:bg-harvestHover disabled:opacity-40"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
